import { supabase, isConfigured } from './supabase';
import type {
  Signal,
  Relationship,
  RelationshipType,
  ClientMetrics,
  HotLead,
  TopReferrer,
  TopPhotographer,
  ConversionAnalytics,
  Contact,
} from '../types';

// ============================================================
// SIGNALS
// ============================================================

export async function getSignalsForContact(contactId: string, limit = 10): Promise<Signal[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_signals')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[intelligence] getSignalsForContact failed:', error);
    return [];
  }

  return data as Signal[];
}

export async function getRecentSignals(
  teamId: string,
  days = 7,
  limit = 20
): Promise<Signal[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_recent_signals', {
    p_team_id: teamId,
    p_days: days,
    p_limit: limit,
  });

  if (error) {
    console.error('[intelligence] getRecentSignals failed:', error);
    return [];
  }

  return (data || []) as Signal[];
}

export async function createSignal(
  teamId: string,
  contactId: string | null,
  type: string,
  title: string,
  payload: Record<string, unknown> = {},
  createTask = false,
  taskDueDays = 1
): Promise<string | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('create_sales_signal', {
    p_team_id: teamId,
    p_contact_id: contactId,
    p_type: type,
    p_title: title,
    p_payload: payload,
    p_create_task: createTask,
    p_task_due_days: taskDueDays,
  });

  if (error) {
    console.error('[intelligence] createSignal failed:', error);
    return null;
  }

  return data as string;
}

// ============================================================
// RELATIONSHIPS (Network Mapping)
// ============================================================

export async function getRelationshipsForContact(contactId: string): Promise<Relationship[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_relationships')
    .select(`
      *,
      from_contact:from_contact_id (id, first_name, last_name, company, email, type),
      to_contact:to_contact_id (id, first_name, last_name, company, email, type)
    `)
    .or(`from_contact_id.eq.${contactId},to_contact_id.eq.${contactId}`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[intelligence] getRelationshipsForContact failed:', error);
    return [];
  }

  return data as Relationship[];
}

export async function createRelationship(
  teamId: string,
  fromContactId: string,
  toContactId: string,
  type: RelationshipType,
  notes?: string
): Promise<Relationship | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_relationships')
    .insert({
      team_id: teamId,
      from_contact_id: fromContactId,
      to_contact_id: toContactId,
      type,
      notes: notes || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[intelligence] createRelationship failed:', error);
    return null;
  }

  return data as Relationship;
}

export async function deleteRelationship(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_relationships')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[intelligence] deleteRelationship failed:', error);
    return false;
  }

  return true;
}

// ============================================================
// CLIENT METRICS & HEALTH
// ============================================================

export async function getClientMetrics(contactId: string): Promise<ClientMetrics | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('get_client_metrics', {
    p_contact_id: contactId,
  });

  if (error) {
    console.error('[intelligence] getClientMetrics failed:', error);
    return null;
  }

  if (data && data.length > 0) {
    return data[0] as ClientMetrics;
  }

  return null;
}

// ============================================================
// HOT LEADS
// ============================================================

export async function getHotLeads(teamId: string, limit = 10): Promise<HotLead[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_hot_leads', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[intelligence] getHotLeads failed:', error);
    return [];
  }

  return (data || []) as HotLead[];
}

// ============================================================
// TOP REFERRERS & PHOTOGRAPHERS
// ============================================================

export async function getTopReferrers(teamId: string, limit = 10): Promise<TopReferrer[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_top_referrers', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[intelligence] getTopReferrers failed:', error);
    return [];
  }

  return (data || []) as TopReferrer[];
}

export async function getTopPhotographersByInfluence(
  teamId: string,
  limit = 10
): Promise<TopPhotographer[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_top_photographers_by_influence', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[intelligence] getTopPhotographersByInfluence failed:', error);
    return [];
  }

  return (data || []) as TopPhotographer[];
}

// ============================================================
// CONVERSION ANALYTICS
// ============================================================

export async function getConversionAnalytics(
  teamId: string,
  days = 30
): Promise<ConversionAnalytics | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('get_conversion_analytics', {
    p_team_id: teamId,
    p_days: days,
  });

  if (error) {
    console.error('[intelligence] getConversionAnalytics failed:', error);
    return null;
  }

  if (data && data.length > 0) {
    return data[0] as ConversionAnalytics;
  }

  return null;
}

// ============================================================
// REFERRAL MANAGEMENT
// ============================================================

export async function setReferrer(
  contactId: string,
  referredByContactId: string | null,
  referralType: string | null
): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_contacts')
    .update({
      referred_by_contact_id: referredByContactId,
      referral_type: referralType,
    })
    .eq('id', contactId);

  if (error) {
    console.error('[intelligence] setReferrer failed:', error);
    return false;
  }

  return true;
}

export async function searchContacts(teamId: string, query: string): Promise<Contact[]> {
  if (!isConfigured || !query || query.length < 2) return [];

  const { data, error } = await supabase
    .from('sales_contacts')
    .select('*')
    .eq('team_id', teamId)
    .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%,company.ilike.%${query}%`)
    .limit(10);

  if (error) {
    console.error('[intelligence] searchContacts failed:', error);
    return [];
  }

  return data as Contact[];
}
